export const initialState = {
  connectors: [],
  assets: [],
  borrowingBase: null,
  offers: [],
  agents: [],
  selectedOffer: null,
  // New marketplace state
  properties: [],
  listings: [],
  investors: [],
  commitments: [],
  currentInvestor: null,
  marketplaceFilters: {
    assetType: 'all',
    ltvRange: [0, 1],
    location: 'all',
    lienPosition: 'all',
    status: 'all'
  },
  ui: {
    toast: null,
    modal: null
  }
};

export const demoReducer = (state, action) => {
  switch (action.type) {
    case 'LOAD_CONNECTORS':
      return {
        ...state,
        connectors: action.payload
      };

    case 'LOAD_ASSETS':
      return {
        ...state,
        assets: action.payload
      };

    case 'LOAD_BORROWING_BASE':
      return {
        ...state,
        borrowingBase: action.payload
      };

    case 'LOAD_OFFERS':
      return {
        ...state,
        offers: action.payload
      };

    case 'LOAD_AGENTS':
      return {
        ...state,
        agents: action.payload
      };

    case 'LOAD_PROPERTIES':
      return {
        ...state,
        properties: action.payload
      };

    case 'LOAD_LISTINGS':
      return {
        ...state,
        listings: action.payload
      };

    case 'LOAD_INVESTORS':
      return {
        ...state,
        investors: action.payload
      };

    case 'LOAD_COMMITMENTS':
      return {
        ...state,
        commitments: action.payload
      };

    case 'CONNECT_SYSTEM':
      return {
        ...state,
        connectors: state.connectors.map(connector =>
          connector.id === action.payload
            ? {
                ...connector,
                status: 'connected',
                lastSync: new Date().toISOString(),
                records: Math.floor(Math.random() * 10000) + 1000
              }
            : connector
        ),
        borrowingBase: state.borrowingBase ? {
          ...state.borrowingBase,
          status: 'ready',
          lastUpdated: new Date().toISOString()
        } : state.borrowingBase
      };

    case 'DISCONNECT_SYSTEM':
      return {
        ...state,
        connectors: state.connectors.map(connector =>
          connector.id === action.payload
            ? {
                ...connector,
                status: 'disconnected',
                lastSync: null,
                records: 0
              }
            : connector
        )
      };

    case 'RUN_AGENT':
      const agent = state.agents.find(a => a.id === action.payload);
      if (!agent) return state;

      let newState = {
        ...state,
        agents: state.agents.map(a =>
          a.id === action.payload
            ? {
                ...a,
                status: 'running',
                lastRun: new Date().toISOString()
              }
            : a
        )
      };

      // Simulate agent impact
      if (agent.type === 'valuation') {
        // Increase borrowing base capacity
        newState.borrowingBase = {
          ...newState.borrowingBase,
          totalCollateralValue: newState.borrowingBase.totalCollateralValue * 1.15,
          maxBorrowingBase: newState.borrowingBase.maxBorrowingBase * 1.15,
          availableCapacity: newState.borrowingBase.availableCapacity * 1.15,
          lastUpdated: new Date().toISOString()
        };
      } else if (agent.type === 'opportunity') {
        // Add new offer
        const newOffer = {
          id: `offer-${Date.now()}`,
          lender: 'AI-Generated Lender',
          amount: Math.floor(Math.random() * 5000000) + 3000000,
          interestRate: 6.5 + Math.random() * 2,
          term: 24,
          advanceRate: 0.75,
          fees: {
            origination: 0.005,
            annual: 0.0025
          },
          status: 'available',
          createdAt: new Date().toISOString(),
          expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
          conditions: [
            'Standard financial reporting',
            'Quarterly reviews'
          ],
          rating: 'A',
          fundingTime: '3-5 business days'
        };
        newState.offers = [...newState.offers, newOffer];
      }

      // Reset agent status after 2 seconds
      setTimeout(() => {
        newState.agents = newState.agents.map(a =>
          a.id === action.payload
            ? { ...a, status: 'idle' }
            : a
        );
      }, 2000);

      return newState;

    case 'SELECT_OFFER':
      return {
        ...state,
        selectedOffer: action.payload
      };

    case 'ACCEPT_OFFER':
      return {
        ...state,
        offers: state.offers.map(offer =>
          offer.id === action.payload
            ? { ...offer, status: 'accepted' }
            : offer
        ),
        borrowingBase: state.borrowingBase ? {
          ...state.borrowingBase,
          currentBorrowingBase: state.borrowingBase.currentBorrowingBase + 
            state.offers.find(o => o.id === action.payload)?.amount || 0,
          utilizationRate: (state.borrowingBase.currentBorrowingBase + 
            (state.offers.find(o => o.id === action.payload)?.amount || 0)) / 
            state.borrowingBase.maxBorrowingBase
        } : state.borrowingBase
      };

    case 'DRAW_FUNDS':
      return {
        ...state,
        borrowingBase: state.borrowingBase ? {
          ...state.borrowingBase,
          currentBorrowingBase: state.borrowingBase.currentBorrowingBase + action.payload,
          availableCapacity: state.borrowingBase.availableCapacity - action.payload,
          utilizationRate: (state.borrowingBase.currentBorrowingBase + action.payload) / 
            state.borrowingBase.maxBorrowingBase
        } : state.borrowingBase
      };

    case 'SHOW_TOAST':
      return {
        ...state,
        ui: {
          ...state.ui,
          toast: action.payload
        }
      };

    case 'HIDE_TOAST':
      return {
        ...state,
        ui: {
          ...state.ui,
          toast: null
        }
      };

    case 'SHOW_MODAL':
      return {
        ...state,
        ui: {
          ...state.ui,
          modal: action.payload
        }
      };

    case 'HIDE_MODAL':
      return {
        ...state,
        ui: {
          ...state.ui,
          modal: null
        }
      };

    // Marketplace actions
    case 'CREATE_LISTING':
      return {
        ...state,
        listings: [...state.listings, action.payload]
      };

    case 'UPDATE_LISTING':
      return {
        ...state,
        listings: state.listings.map(listing =>
          listing.id === action.payload.id ? { ...listing, ...action.payload } : listing
        )
      };

    case 'COMMIT_TO_LISTING':
      const newCommitment = action.payload;
      const updatedListings = state.listings.map(listing => {
        if (listing.id === newCommitment.listingId) {
          const newCommittedAmount = listing.committedAmount + newCommitment.amount;
          const newInvestorCount = listing.investorCount + 1;
          const newStatus = newCommittedAmount >= listing.requestedAmount ? 'Matched' : listing.status;
          
          return {
            ...listing,
            committedAmount: newCommittedAmount,
            investorCount: newInvestorCount,
            status: newStatus
          };
        }
        return listing;
      });
      
      return {
        ...state,
        listings: updatedListings,
        commitments: [...state.commitments, newCommitment]
      };

    case 'SIGNUP_INVESTOR':
      return {
        ...state,
        investors: [...state.investors, action.payload],
        currentInvestor: action.payload
      };

    case 'UPDATE_INVESTOR_MANDATES':
      return {
        ...state,
        investors: state.investors.map(investor =>
          investor.id === action.payload.id ? { ...investor, mandates: action.payload.mandates } : investor
        ),
        currentInvestor: state.currentInvestor?.id === action.payload.id 
          ? { ...state.currentInvestor, mandates: action.payload.mandates }
          : state.currentInvestor
      };

    case 'UPDATE_MARKETPLACE_FILTERS':
      return {
        ...state,
        marketplaceFilters: { ...state.marketplaceFilters, ...action.payload }
      };

    case 'RUN_ENCUMBRANCE_AGENT':
      // Simulate encumbrance agent detecting conflicts
      const conflictedListings = state.listings.map(listing => {
        const property = state.properties.find(p => p.id === listing.assetId);
        if (property && property.inUnderwriting) {
          return {
            ...listing,
            badges: [...listing.badges, 'In Underwriting']
          };
        }
        return listing;
      });
      
      return {
        ...state,
        listings: conflictedListings,
        agents: state.agents.map(agent =>
          agent.id === 'encumbrance-agent'
            ? { ...agent, status: 'running', lastRun: new Date().toISOString() }
            : agent
        )
      };

    default:
      return state;
  }
};
